import { MetaTag } from '../../../loader/essentials/init-metatag.js';
import { log } from '../../../loader/essentials/logger.js';
import { Matcher, waitForPostMessage } from '../../../utils/wait-for-postmessage.js';

declare const window: Window & {
    SDG?: MetaTag;
};

const START_RENDER_MATCHER: Matcher<{ slotName: string }> = {
    regex: /^SDG_START_RENDER#([a-zA-Z0-9_-]+)/,
    extract: (match) => ({
        slotName: match[1],
    }),
};

const AD_DELIVERED: Matcher<{ slotName: string, width: number, height: number }> = {
    regex: /^SDG_AD_DELIVERED#([a-zA-Z0-9_-]+)#size([0-9]+)x([0-9]+)/,
    extract: (match) => ({
        slotName: match[1],
        width: parseInt(match[2]),
        height: parseInt(match[3])
    })
};

const SDG_FINISH_RENDER: Matcher<{ slotName: string }> = {
    regex: /^SDG_FINISH_RENDER#([a-zA-Z0-9_-]+)/,
    extract: (match) => ({
        slotName: match[1],
    }),
};

waitForPostMessage(START_RENDER_MATCHER, ({ slotName }) => {
    const adslot = window.SDG?.Advertising?.getSlotByName(slotName);
    if (!adslot) return log.error('SYSTEM: can not found slotName: %s reported by postMessage "START_RENDER_MATCHER"', [slotName]);

    //Start render process, do not await
    adslot.renderCreative(new Promise<void>(resolve => {

        //When postMessage "SDG_FINISH_RENDER" is received, consider rendering to be complete.
        const waiter = waitForPostMessage(SDG_FINISH_RENDER, ({ slotName }) => {
            if (adslot.slotName === slotName) { //make sure postMessage fits the slot used before
                waiter.cancel()
                window.clearTimeout(timer)
                resolve();
            }
        }, true);

        //if finish message is not received in 25 secs, fake it and let adslot finish rendering
        const timer = window.setTimeout(() => {
            log.error('%s: reported third party creative, but never received render complete message. Advancing without creative being fully rendered.', [slotName.toUpperCase()]);
            waiter.cancel();
            resolve();
        }, 25000);

    }));
}, false);

/**
 * Used by 3rd parties to let us know what dimensions their creative will use.
 * Will help with CLS avoidance and makes sure adslots are large enough to display this ad.
 */
waitForPostMessage(AD_DELIVERED, ({ slotName, width, height }) => {
    window.SDG?.Advertising?.getSlotByName(slotName)?.reportAdDelivery(width, height, false);
}, false);

export class PromiseQueue {
    /** store promises in a Set to ensure uniqueness and efficient deletion */
    #promises = new Set<Promise<any>>();

    #onStartCallbacks = new Set<() => void>();
    #onEndCallbacks = new Set<() => void>();

    /** timer for delaying execution of end callbacks after the last promise is completed */
    #timer = -1;

    /**
     * Initializes a new instance of the PromiseQueue class.
     *
     * @param closingDelay The delay (in milliseconds) before executing end callbacks after all promises are completed.
     */
    constructor(
        private readonly closingDelay: number = 25,
    ) { }

    /**
     * Adds a promise to the queue and manages its lifecycle.
     *
     * @param promise The promise to add to the queue.
     */
    public add<Type>(promise: Promise<Type>) {
        // if this is the first promise, trigger start callbacks
        if (this.#promises.size === 0) this.#onStartCallbacks.forEach(callback => callback());

        // new command received, halt timer if running
        window.clearTimeout(this.#timer);

        // add promise to collection
        this.#promises.add(promise);

        // when promise is resolved or rejected, update internal data
        promise.finally(() => {
            // remove promise from collection
            this.#promises.delete(promise);

            // if last one done, timer will wait shortly for new commands before end
            if (this.#promises.size === 0) {
                // cancel previous timer if running
                window.clearTimeout(this.#timer);
                // setup new timer to trigger end callbacks
                this.#timer = window.setTimeout(() => this.#onEndCallbacks.forEach(callback => callback()), this.closingDelay);
            }
        });
        return promise;
    }

    /**
     * Adds a callback function to execute when promises start.
     *
     * @param callback The callback function to execute when promises start.
     * @returns The PromiseQueue instance for chaining.
     */
    public onStart(callback: () => void) {
        this.#onStartCallbacks.add(callback);
        return this;
    }

    /**
     * Adds a callback function to execute when all promises are completed.
     *
     * @param callback The callback function to execute when all promises are completed.
     * @returns The PromiseQueue instance for chaining.
     */
    public onEnd(callback: () => void) {
        this.#onEndCallbacks.add(callback);
        return this;
    }
}
